import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { supabase } from "../../lib/supabase";
import { stripePromise } from "../../lib/stripe";
import { syncCards, createSetupIntent, attachCard, setPrimaryCard, deleteCard } from "../../services/stripeCardsService";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "14px", color: "#151515", fontFamily: "inherit", "::placeholder": { color: "#7b7b7b" } },
    invalid: { color: "#e53935" },
  },
};

export function cardBrandLabel(brand) {
  return brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
}

// Brand badges, hand-drawn to be actually recognizable at a glance rather
// than a generic colored-line icon: Mastercard gets its real two-circle
// mark; Visa/Amex/Discover get a solid brand-colored card face with a
// short white wordmark (the same simplified-badge convention Stripe's own
// UI and most card lists use). Unknown brands fall back to a plain neutral
// card outline. 32x22 — close to a real card's aspect ratio.
const BRAND_WORDMARKS = {
  visa: { fill: "#1434CB", label: "VISA", italic: true },
  amex: { fill: "#006FCF", label: "AMEX" },
  discover: { fill: "#FF6000", label: "DISC" },
};

function CardBrandIcon({ brand }) {
  const normalized = (brand || "").toLowerCase();

  if (normalized === "mastercard") {
    return (
      <svg width="32" height="22" viewBox="0 0 32 22" fill="none" aria-hidden="true" style={{ flexShrink:0 }}>
        <rect x="0.75" y="0.75" width="30.5" height="20.5" rx="4" fill="#F0F0F0" stroke="#CFCFCF" />
        <circle cx="13" cy="11" r="6" fill="#EB001B" />
        <circle cx="19" cy="11" r="6" fill="#F79E1B" fillOpacity="0.85" />
      </svg>
    );
  }

  const wordmark = BRAND_WORDMARKS[normalized];
  if (wordmark) {
    return (
      <svg width="32" height="22" viewBox="0 0 32 22" fill="none" aria-hidden="true" style={{ flexShrink:0 }}>
        <rect width="32" height="22" rx="4" fill={wordmark.fill} />
        <text
          x="16" y="14.5" textAnchor="middle" fill="#FFFFFF"
          fontSize="7.5" fontWeight="700" fontStyle={wordmark.italic ? "italic" : "normal"}
          fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.3"
        >
          {wordmark.label}
        </text>
      </svg>
    );
  }

  return (
    <svg width="32" height="22" viewBox="0 0 32 22" fill="none" aria-hidden="true" style={{ flexShrink:0 }}>
      <rect x="0.75" y="0.75" width="30.5" height="20.5" rx="4" fill="#F0F0F0" stroke="#CFCFCF" />
    </svg>
  );
}

function AddCardForm({ advertiserId, authToken, onAdded, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    try {
      const { client_secret } = await createSetupIntent(advertiserId, authToken);
      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card: cardElement },
      });
      if (confirmError) {
        setError(confirmError.message || "Failed to save card");
        return;
      }
      await attachCard(advertiserId, authToken, setupIntent.payment_method);
      onAdded();
    } catch (err) {
      setError(err?.message || "Failed to save card");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ padding:"12px 14px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10, marginBottom:8 }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      <div style={{ marginBottom:10, fontSize:11, color:"#7b7b7b" }}>
        Payments are processed securely by Stripe. We never see or store your card number.
      </div>
      {error && (
        <div style={{ marginBottom:10, fontSize:12, color:"#e53935" }}>{error}</div>
      )}
      <div style={{ display:"flex", gap:8 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ flex:1, padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor: saving ? "not-allowed" : "pointer" }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !stripe}
          style={{ flex:1, padding:"10px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
        >
          {saving ? "Saving…" : "Save card"}
        </button>
      </div>
    </form>
  );
}

// CardManagementSection — advertiser self-service credit card management
// (DEV-2006). Unlike BillingMethodSection's own dropdown, this is not
// support-gated: the advertiser (any member with canManageBilling — same
// flag BillingDetailsSection uses) owns their own cards; other team members
// get a read-only card list. On first load with no local cards, silently
// syncs against Stripe in case a Customer/card already exists for this
// advertiser from outside this app. Every write (sync included) is also
// re-checked server-side in stripe-cards via requireBillingPermission —
// this prop only controls what's offered in the UI, it isn't the security
// boundary.
//
// Rendered *inside* BillingMethodSection's own card (after the saved
// billing-method value, only when that saved value is 'credit_card') rather
// than as its own standalone card — cards on file are a detail of the
// credit_card billing method, not a separate concern, so this component has
// no outer background/border/padding of its own; the divider below is the
// only visual seam between "which method" and "which cards."
export function CardManagementSection({ advertiserId, authToken, billingMethod, canManageBilling, loading }) {
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncBanner, setSyncBanner] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [busyCardId, setBusyCardId] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // "primary" | "delete" | null
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

  const fetchCards = useCallback(async () => {
    if (!supabase || !advertiserId) return [];
    const { data, error: fetchError } = await supabase
      .from("advertiser_payment_methods")
      .select("*")
      .eq("advertiser_id", advertiserId)
      .order("created_at", { ascending: true });
    if (fetchError) {
      setError(fetchError.message || "Failed to load cards");
      return [];
    }
    return data ?? [];
  }, [advertiserId]);

  useEffect(() => {
    if (loading || billingMethod !== "credit_card") return;
    if (!advertiserId || !authToken) { setCardsLoading(false); return; }
    let cancelled = false;
    (async () => {
      setCardsLoading(true);
      setError(null);
      setSyncBanner(null);
      try {
        const existing = await fetchCards();
        if (cancelled) return;
        if (existing.length > 0 || !canManageBilling) {
          // Non-billing members get a read-only view of whatever's already
          // synced — syncing is a write (attaches a Stripe Customer, upserts
          // cards) and stripe-cards would reject it for them anyway.
          setCards(existing);
          return;
        }
        const result = await syncCards(advertiserId, authToken);
        if (cancelled) return;
        if (result.matched === false) {
          setSyncBanner(result.reason || "No card on file yet — add one below");
          setCards([]);
        } else {
          setCards(result.cards ?? []);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load cards");
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, billingMethod, advertiserId, authToken, canManageBilling, fetchCards]);

  const refresh = useCallback(async () => {
    const existing = await fetchCards();
    setCards(existing);
  }, [fetchCards]);

  // If billing permission is lost mid-flow (e.g. a masquerade/context
  // switch while the add-card form is open), close it — createSetupIntent
  // is still enforced server-side via requireBillingPermission (the real
  // boundary, per this app's masquerade-is-read-only convention), but the
  // form itself shouldn't keep offering an action the context no longer
  // supports.
  useEffect(() => {
    if (!canManageBilling) setShowAddForm(false);
  }, [canManageBilling]);

  const handleAdded = async () => {
    setShowAddForm(false);
    setSyncBanner(null);
    await refresh();
  };

  // Tracks which action is in flight, not just which card — otherwise
  // clicking "Set as primary" also flips "Remove" to "Removing…" on the
  // same row, since both buttons would key off the same busy card id.
  const runCardAction = async (cardId, actionType, action, errMsg) => {
    setBusyCardId(cardId);
    setBusyAction(actionType);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (err) {
      setError(err?.message || errMsg);
    } finally {
      setBusyCardId(null);
      setBusyAction(null);
    }
  };

  const handleSetPrimary = (cardId) =>
    runCardAction(cardId, "primary", () => setPrimaryCard(advertiserId, authToken, cardId), "Failed to set primary card");

  const handleDelete = (cardId) => {
    setConfirmingDeleteId(null);
    return runCardAction(cardId, "delete", () => deleteCard(advertiserId, authToken, cardId), "Failed to remove card");
  };

  // Primary always leads; the rest sort alphabetically by brand so the list
  // order doesn't just reflect insertion order (created_at from the DB).
  const sortedCards = useMemo(() => {
    return [...cards].sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return cardBrandLabel(a.brand).localeCompare(cardBrandLabel(b.brand));
    });
  }, [cards]);

  if (billingMethod !== "credit_card") return null;

  return (
    <>
      <div style={{ borderTop:"1px solid #E2E2E2", margin:"16px 0 12px" }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1 }}>Cards on File</div>
        {!cardsLoading && !showAddForm && canManageBilling && (
          <div style={{ display:"flex", gap:8 }}>
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add card
            </button>
          </div>
        )}
      </div>

      {cardsLoading ? (
        <div style={{ fontSize:12, color:"#7b7b7b" }}>Loading…</div>
      ) : (
        <>
          {syncBanner && (
            <div style={{ marginBottom:10, padding:10, background:"#FFF5FB", border:"1px solid #FFE9F6", borderRadius:8, fontSize:12, color:"#3E3E3E" }}>
              {syncBanner}
            </div>
          )}

          {error && (
            <div style={{ marginBottom:10, fontSize:12, color:"#e53935" }}>{error}</div>
          )}

          {sortedCards.length === 0 && !showAddForm && (
            <div style={{ marginBottom:4 }}>
              <div style={{ fontSize:13, color:"#3E3E3E" }}>No cards added yet.</div>
              <div style={{ fontSize:12, color:"#7b7b7b" }}>Add a card to use for billing.</div>
            </div>
          )}

          {sortedCards.map((card) => (
            <div
              key={card.id}
              style={{ padding:"12px 14px", marginBottom:8, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10 }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
                <CardBrandIcon brand={card.brand} />
                <div style={{ minWidth:0, flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:8, fontSize:14, fontWeight:600, color:"#151515" }}>
                    <span style={{ whiteSpace:"nowrap" }}>{cardBrandLabel(card.brand)} •••• {card.last4}</span>
                    {card.is_primary && (
                      <span style={{ flexShrink:0, padding:"2px 8px", background:"#FFE9F6", color:"#F90091", borderRadius:999, fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>Primary</span>
                    )}
                  </div>
                  <div style={{ marginTop:2, fontSize:12, color:"#7b7b7b" }}>Expires {card.exp_month}/{card.exp_year}</div>
                </div>
              </div>
              {canManageBilling && (
                // Own row below the card identity, never beside it — at the
                // panel's actual width (this renders inside UserMenu's side
                // panel, not a full dashboard column) icon + masked number +
                // Primary badge + both buttons never fit on one line without
                // the buttons overlapping the text.
                <div style={{ display:"flex", justifyContent:"space-between", gap:8, marginTop:10 }}>
                  {!card.is_primary && (() => {
                    const isSettingPrimary = busyCardId === card.id && busyAction === "primary";
                    const isBusy = busyCardId === card.id;
                    return (
                      <button
                        type="button"
                        onClick={() => handleSetPrimary(card.id)}
                        disabled={isBusy}
                        style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:11, fontWeight:600, whiteSpace:"nowrap", cursor: isSettingPrimary ? "wait" : isBusy ? "not-allowed" : "pointer", opacity: isBusy ? 0.6 : 1 }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        {isSettingPrimary ? "Setting…" : "Set as primary"}
                      </button>
                    );
                  })()}
                  {confirmingDeleteId === card.id ? (
                    // Same inline-confirm pattern as team-member removal in
                    // UserMenu.jsx (confirmingRemovalId) — a mis-click on
                    // Remove here permanently detaches a live payment method,
                    // so it gets the same one-more-tap guard.
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flex:1, padding:"4px 4px 4px 10px", background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8 }}>
                      <span style={{ fontSize:11, color:"#7b1e1c" }}>Remove this card?</span>
                      <div style={{ display:"flex", gap:6 }}>
                        <button
                          type="button"
                          onClick={() => setConfirmingDeleteId(null)}
                          style={{ padding:"4px 8px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer" }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(card.id)}
                          style={{ padding:"4px 8px", background:"#e53935", color:"#fff", border:"none", borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer" }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Red-tinted border (not the neutral #E2E2E2 ghost-button
                    // border) — matches the destructive-action convention
                    // already established for the team-remove button in
                    // UserMenu.jsx (border #fca5a5 + text #e53935). The trash
                    // icon path is copied from that same button for the same
                    // reason — one "remove" icon, everywhere in the app.
                    <button
                      type="button"
                      onClick={() => setConfirmingDeleteId(card.id)}
                      disabled={busyCardId === card.id}
                      style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", background:"transparent", color:"#e53935", border:"1px solid #fca5a5", borderRadius:8, fontSize:11, fontWeight:600, whiteSpace:"nowrap", cursor: busyCardId === card.id ? "not-allowed" : "pointer", opacity: busyCardId === card.id ? 0.6 : 1, marginLeft: card.is_primary ? "auto" : 0 }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                      </svg>
                      {busyCardId === card.id && busyAction === "delete" ? "Removing…" : "Remove"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

          {showAddForm && stripePromise && (
            <div style={{ marginTop: sortedCards.length > 0 ? 12 : 0 }}>
              <Elements stripe={stripePromise}>
                <AddCardForm
                  advertiserId={advertiserId}
                  authToken={authToken}
                  onAdded={handleAdded}
                  onCancel={() => setShowAddForm(false)}
                />
              </Elements>
            </div>
          )}

          {showAddForm && !stripePromise && (
            <div style={{ marginTop:10, fontSize:12, color:"#e53935" }}>
              Card payments are not configured for this environment.
            </div>
          )}
        </>
      )}
    </>
  );
}
